/**
 * Shrinks the Trustpilot review widget when a product has no reviews.
 *
 * Trustpilot renders its empty state ("No reviews for this product yet") inside
 * an iframe sized to the viewport, not to its content - roughly 150px of content
 * in a 765px frame. Whether a product has reviews is only known inside that
 * cross-origin iframe, so it cannot be decided in Liquid.
 *
 * The trick is that data-no-reviews="collapse" makes Trustpilot tell us: it sets
 * display:none on the iframe when there is nothing to show. So the widget is
 * rendered with collapse to get that signal, and the moment it fires the widget
 * is re-rendered with no-reviews="show" at a short height.
 *
 * Nothing is removed - the empty state stays visible, it just stops reserving
 * the height of a full review list.
 */
if (!customElements.get('reviews-empty-height')) {
  customElements.define(
    'reviews-empty-height',
    class ReviewsEmptyHeight extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;
        this.bound = true;

        // .tp-widget, not .trustpilot-widget: the latter is only added once
        // trustpilot.js renders the widget, which is after this runs.
        this.widget = this.querySelector('.tp-widget, .trustpilot-widget');
        if (!this.widget) return;

        this.emptyHeight = this.dataset.emptyHeight || '200px';
        this.watch();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      watch() {
        // Trustpilot injects and later replaces the iframe, so the whole widget
        // is observed rather than one node.
        this.observer = new MutationObserver(() => this.check());
        this.observer.observe(this.widget, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style'],
        });

        this.check();
      }

      check() {
        if (this.swapped) return;

        const frame = this.widget.querySelector('iframe');
        if (!frame) return;

        // Collapsed is Trustpilot's way of saying "no reviews for this SKU".
        const collapsed = frame.style.display === 'none' || window.getComputedStyle(frame).display === 'none';
        if (!collapsed) return;

        this.swapped = true;
        this.showCompactEmptyState();
      }

      showCompactEmptyState() {
        if (!window.Trustpilot || typeof window.Trustpilot.loadFromElement !== 'function') return;

        this.observer.disconnect();

        this.widget.setAttribute('data-no-reviews', 'show');
        this.widget.setAttribute('data-style-height', this.emptyHeight);
        // A fixed-height widget also holds that height as an inline min-height.
        this.widget.style.minHeight = '';
        this.widget.removeAttribute('data-tp-loaded');
        this.widget.innerHTML = '';

        window.Trustpilot.loadFromElement(this.widget, true);
        this.classList.add('reviews-empty');
      }
    }
  );
}
